/**
 * FE#119 — classify a scheduled message into a send-log `messageType`
 * (text | image | video | audio | document | media | poll).
 *
 * TWO bugs this fixes:
 *  1. Poll short-circuit: `scheduleMessageTool` seeds an empty poll scaffold
 *     ({question:'', options:[…]}) on EVERY message, poll or not. The old
 *     `if (isPoll || userMessage.poll) return 'poll'` therefore stamped ~all
 *     sends 'poll' AND short-circuited before the media branch, hiding real
 *     audio/image/etc. A message is a poll ONLY when explicitly flagged
 *     (`isPoll`) or its poll carries a real question.
 *  2. MIME kinds (#174): `asset.type` is almost always a MIME string
 *     ('audio/mpeg', 'image/png', 'application/pdf'), so the old bare-word
 *     `kind === 'audio'` equality never matched → everything fell to 'media'.
 *     Match the MIME family prefix, keeping the legacy bare-word kinds.
 *
 * Kept as a pure function so classification is unit-testable without the
 * (heavy) send thunk.
 */
export interface ClassifiableMessage {
  isPoll?: boolean;
  poll?: { question?: string } | null;
  asset?: { url?: string | null; type?: string | null; mediaType?: string | null } | null;
}

export function classifyMessageType(userMessage: ClassifiableMessage | null | undefined): string {
  const hasRealPoll =
    !!userMessage?.isPoll ||
    !!(userMessage?.poll?.question && String(userMessage.poll.question).trim());
  if (hasRealPoll) return 'poll';

  const asset = userMessage?.asset;
  if (asset && String(asset.url || '').trim()) {
    const raw = String(asset.type || asset.mediaType || '').toLowerCase();
    const family = raw.split('/')[0];
    if (family === 'image' || raw === 'image') return 'image';
    if (family === 'video' || raw === 'video') return 'video';
    if (family === 'audio' || raw === 'audio' || raw === 'voice' || raw === 'ptt') return 'audio';
    if (family === 'application' || raw === 'document') return 'document';
    return 'media';
  }

  return 'text';
}
